import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero, { Breadcrumb } from "@/components/feature/PageHero";
import TitleCard from "@/components/feature/TitleCard";
import NewsCard from "@/components/feature/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import { titles } from "@/mocks/titles";
import { newsItems } from "@/mocks/news";

const scopes = [
  { value: "all", label: "全部内容" },
  { value: "title", label: "影视作品" },
  { value: "news", label: "影视资讯" },
] as const;

type Scope = (typeof scopes)[number]["value"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = (params.get("q") ?? "").trim();
  const scopeParam = (params.get("scope") ?? "all") as Scope;
  const scope: Scope = scopes.some((item) => item.value === scopeParam) ? scopeParam : "all";
  const [draft, setDraft] = useState(query);

  useSeo({
    title: query ? `“${query}” 搜索结果 - 星光传媒` : "全站搜索 - 星光传媒",
    description: query
      ? `星光传媒全站搜索“${query}”的结果，包含相关影视作品与影视资讯，支持按内容类型过滤。`
      : "星光传媒全站搜索，支持关键词检索影视作品与影视资讯。",
    keywords: "星光传媒,影视搜索,电影搜索,剧集搜索,资讯搜索",
    canonicalPath: "/search",
  });

  const matchedTitles = useMemo(() => {
    if (!query) return [];
    return titles.filter((item) => {
      const text = [
        item.name,
        item.enName,
        item.tagline,
        item.synopsis,
        item.director,
        item.region,
        ...item.genres,
        ...item.cast.map((member) => member.name),
      ].join(" ");
      return text.toLowerCase().includes(query.toLowerCase());
    });
  }, [query]);

  const matchedNews = useMemo(() => {
    if (!query) return [];
    return newsItems.filter((item) => {
      const text = [item.title, item.summary, item.category, ...item.tags].join(" ");
      return text.toLowerCase().includes(query.toLowerCase());
    });
  }, [query]);

  const showTitles = scope !== "news" ? matchedTitles : [];
  const showNews = scope !== "title" ? matchedNews : [];
  const total = showTitles.length + showNews.length;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextParams = new URLSearchParams();
    if (draft.trim()) nextParams.set("q", draft.trim());
    if (scope !== "all") nextParams.set("scope", scope);
    setParams(nextParams, { replace: true });
  };

  const changeScope = (next: Scope) => {
    const nextParams = new URLSearchParams(params);
    if (next === "all") nextParams.delete("scope");
    else nextParams.set("scope", next);
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLabel="首页" />
      <main>
        <PageHero
          eyebrow="Search"
          title="全站搜索"
          description="输入电影名、剧集名、演员、导演或资讯关键词，一次搜索覆盖全部影视作品与资讯内容。"
          background="https://readdy.ai/api/search-image?query=Cinematic%20dark%20library%20of%20film%20reels%20and%20index%20cards%20with%20a%20single%20warm%20amber%20light%2C%20deep%20navy%20tones%2C%20moody%20minimal%20composition%2C%20high%20detail&width=1600&height=900&seq=search-hero-bg&orientation=landscape"
        >
          <form onSubmit={submit} className="mt-6">
            <div className="flex items-center gap-2 bg-background-50 rounded-md p-1.5 max-w-2xl">
              <i className="ri-search-line text-lg text-foreground-400 ml-2 w-5 h-5 flex items-center justify-center"></i>
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="例如：长夜、悬疑、杜望川"
                aria-label="全站搜索关键词"
                className="flex-1 min-w-0 h-10 bg-transparent text-sm md:text-base text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="h-10 px-5 rounded bg-primary-500 text-background-50 text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>
        </PageHero>

        <div className="px-4 md:px-6 py-5 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  {query ? `“${query}” 的搜索结果` : "输入关键词开始搜索"}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  {query ? `共找到 ${total} 条相关内容` : "也可以直接浏览全部影视作品与最新资讯"}
                  <span className="text-foreground-400"> · 演示数据检索</span>
                </p>
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 self-start">
                {scopes.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => changeScope(item.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      scope === item.value ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {total === 0 ? (
              <div className="py-16 md:py-20 text-center bg-background-100 rounded-lg">
                <span className="inline-flex w-14 h-14 rounded-full bg-background-50 text-foreground-400 items-center justify-center mb-4">
                  <i className="ri-search-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                </span>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">
                  {query ? "没有找到相关内容" : "还没有搜索关键词"}
                </h3>
                <p className="text-sm text-foreground-500 mb-5">
                  {query ? "换个关键词试试，或者直接浏览分类与资讯频道。" : "试试搜索“长夜”“悬疑”“星尘列车”等关键词。"}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/category"
                    className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
                  >
                    浏览影视分类
                  </Link>
                  <Link
                    to="/news"
                    className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
                  >
                    查看影视资讯
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                {showTitles.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">影视作品</h3>
                      <span className="text-xs text-foreground-400">{showTitles.length} 部</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-5">
                      {showTitles.map((item) => (
                        <TitleCard key={item.id} title={item} />
                      ))}
                    </div>
                  </div>
                )}

                {showNews.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">影视资讯</h3>
                      <span className="text-xs text-foreground-400">{showNews.length} 条</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      {showNews.map((item) => (
                        <NewsCard key={item.id} item={item} variant="horizontal" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}